/*
 * <twc-clubhouse-index> — the three moving parts of the Clubhouses index:
 * the state tabs, the "See Nearby Clubhouses" sort and the pagination.
 *
 * The section (sections/twc-clubhouse-index.liquid) renders EVERY clubhouse
 * card up front, with the state and the coordinates on each card, already in
 * the "All Clubhouses" order: the listed states in the setting's order, then
 * any other state, then INT, A–Z by name inside each. This element keeps that
 * DOM order for the tabs and the paging; only the nearby sort re-orders.
 * It only ever hides cards it could already show and re-orders the ones it
 * has, so a page with no JavaScript is a complete, readable list — never an
 * empty shell.
 *
 * The section reads the clubhouses inside a Liquid `paginate` of 250, the
 * largest page Liquid allows, and renders only that first page; the paging
 * the visitor sees is this element's, over the cards already in the DOM.
 *
 * GEOLOCATION runs from the button press only — assets/twc-geo.js owns the
 * browser handshake and the distance arithmetic and has no UI of its own, so
 * every word below comes from the section's JSON i18n block.
 *
 * Teardown: one AbortController carries every listener, dropped in
 * disconnectedCallback (theme convention).
 */
(function (global) {
  'use strict';

  if (global.customElements && global.customElements.get('twc-clubhouse-index')) return;

  var CHEVRON_PREVIOUS =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
    '<path d="M10.5 13.5 5 8l5.5-5.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON_NEXT =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
    '<path d="M5.5 2.5 11 8l-5.5 5.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ---- 1. helpers ------------------------------------------------------- */

  function readI18n(host) {
    var tag = host.querySelector('script[data-twc-i18n]');
    if (!tag) return {};
    try {
      return JSON.parse(tag.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  /** Shopify translations carry `{{ name }}` placeholders, spaces optional. */
  function fill(template, name, value) {
    if (!template) return String(value);
    return String(template)
      .replace('{{ ' + name + ' }}', value)
      .replace('{{' + name + '}}', value);
  }

  /** A whole number past 10 km, one decimal below it — a street-level read. */
  function formatDistance(template, km) {
    var rounded = km < 10 ? km.toFixed(1) : String(Math.round(km));
    return fill(template, 'km', rounded);
  }

  /**
   * The board draws "1 2 … 8": the first page, a run around the current one
   * and the last page, with an ellipsis wherever the run skips.
   * Returns page numbers with `null` for each gap.
   */
  function pageWindow(total, current) {
    var out = [];
    var index;

    if (total <= 5) {
      for (index = 1; index <= total; index += 1) out.push(index);
      return out;
    }

    var wanted = { 1: true };
    wanted[total] = true;
    for (index = current - 1; index <= current + 1; index += 1) {
      if (index >= 1 && index <= total) wanted[index] = true;
    }

    var pages = Object.keys(wanted)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      });

    var previous = 0;
    pages.forEach(function (page) {
      if (previous && page - previous > 1) out.push(null);
      out.push(page);
      previous = page;
    });

    return out;
  }

  /* ---- 2. the element --------------------------------------------------- */

  var TwcClubhouseIndex = class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      var signal = this.controller.signal;
      var self = this;

      this.copy = readI18n(this);
      this.grid = this.querySelector('[data-twc-grid]');
      this.pagination = this.querySelector('[data-twc-pagination]');
      this.notice = this.querySelector('[data-twc-geo-notice]');
      this.geoButton = this.querySelector('[data-twc-geo]');
      this.geoLabel = this.querySelector('[data-twc-geo-label]');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-twc-tab]'));
      this.cards = this.grid
        ? Array.prototype.slice.call(this.grid.querySelectorAll('[data-twc-card]'))
        : [];
      this.order = this.cards.slice();

      var perPage = parseInt(this.getAttribute('data-per-page'), 10);
      this.perPage = perPage > 0 ? perPage : 9;
      this.state = 'all';
      this.page = 1;

      if (!this.grid || this.cards.length === 0) return;

      this.tabs.forEach(function (tab) {
        tab.addEventListener(
          'click',
          function () {
            self.selectState(tab.getAttribute('data-twc-tab') || 'all');
          },
          { signal: signal }
        );
      });

      if (this.pagination) {
        this.pagination.addEventListener(
          'click',
          function (event) {
            var target = event.target.closest('[data-twc-page], [data-twc-page-step]');
            if (!target || target.disabled) return;
            var step = target.getAttribute('data-twc-page-step');
            if (step) {
              self.goToPage(self.page + (step === 'previous' ? -1 : 1), true);
              return;
            }
            self.goToPage(parseInt(target.getAttribute('data-twc-page'), 10), true);
          },
          { signal: signal }
        );
      }

      if (this.geoButton) {
        this.geoButton.addEventListener(
          'click',
          function () {
            self.findNearby();
          },
          { signal: signal }
        );
      }

      this.render(false);
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.cards = [];
      this.order = [];
      this.tabs = [];
    }

    /* ---- tabs ----------------------------------------------------------- */

    selectState(state) {
      this.state = state;
      this.page = 1;
      this.tabs.forEach(function (tab) {
        var active = (tab.getAttribute('data-twc-tab') || 'all') === state;
        tab.setAttribute('aria-pressed', active ? 'true' : 'false');
        tab.classList.toggle('twc-community__tab--active', active);
      });
      this.render(false);
    }

    /* ---- paging --------------------------------------------------------- */

    goToPage(page, moveView) {
      if (!isFinite(page)) return;
      this.page = page;
      this.render(false);
      if (moveView) this.bringIntoView();
    }

    bringIntoView() {
      var reduce =
        global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }

    render(reorder) {
      var self = this;

      if (reorder) {
        this.order.forEach(function (card) {
          self.grid.appendChild(card);
        });
      }

      var visible = this.order.filter(function (card) {
        return self.state === 'all' || card.getAttribute('data-twc-state') === self.state;
      });

      var totalPages = Math.max(1, Math.ceil(visible.length / this.perPage));
      if (this.page > totalPages) this.page = totalPages;
      if (this.page < 1) this.page = 1;

      var start = (this.page - 1) * this.perPage;
      this.cards.forEach(function (card) {
        card.hidden = true;
      });
      visible.slice(start, start + this.perPage).forEach(function (card) {
        card.hidden = false;
      });

      this.renderPagination(totalPages);
    }

    renderPagination(totalPages) {
      if (!this.pagination) return;

      while (this.pagination.firstChild) {
        this.pagination.removeChild(this.pagination.firstChild);
      }

      if (totalPages <= 1) {
        this.pagination.hidden = true;
        return;
      }
      this.pagination.hidden = false;

      this.pagination.appendChild(
        this.buildStep('previous', CHEVRON_PREVIOUS, this.copy.pagination_previous, this.page === 1)
      );

      var self = this;
      pageWindow(totalPages, this.page).forEach(function (page) {
        if (page === null) {
          var gap = document.createElement('span');
          gap.className = 'twc-community__page twc-community__page--gap';
          gap.setAttribute('aria-hidden', 'true');
          gap.textContent = '…';
          self.pagination.appendChild(gap);
          return;
        }

        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'twc-community__page';
        button.setAttribute('data-twc-page', String(page));
        button.setAttribute('aria-label', fill(self.copy.pagination_page, 'number', page));
        if (page === self.page) button.setAttribute('aria-current', 'page');
        button.textContent = String(page);
        self.pagination.appendChild(button);
      });

      this.pagination.appendChild(
        this.buildStep('next', CHEVRON_NEXT, this.copy.pagination_next, this.page === totalPages)
      );
    }

    buildStep(action, icon, label, disabled) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'twc-community__page-step';
      button.setAttribute('data-twc-page-step', action);
      button.setAttribute('aria-label', label || action);
      button.disabled = !!disabled;
      // Constant markup, no page data — safe to set as a string.
      button.innerHTML = icon;
      return button;
    }

    /* ---- nearest first --------------------------------------------------- */

    findNearby() {
      var self = this;

      if (!global.TwcGeo || !global.TwcGeo.isSupported()) {
        this.showNotice('unsupported');
        return;
      }

      this.setBusy(true);
      global.TwcGeo.requestPosition()
        .then(function (position) {
          self.setBusy(false);
          self.sortByDistance(position);
        })
        .catch(function (error) {
          self.setBusy(false);
          self.showNotice((error && error.code) || 'unavailable');
        });
    }

    setBusy(busy) {
      if (!this.geoButton) return;
      this.geoButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.geoButton.disabled = !!busy;
      if (this.geoLabel && busy) this.geoLabel.textContent = this.copy.nearby_busy || '';
    }

    sortByDistance(position) {
      var self = this;
      var sorted = global.TwcGeo.haversineSort(this.cards, position);

      this.order = sorted.map(function (entry) {
        var slot = entry.item.querySelector('[data-twc-distance]');
        if (slot) {
          if (entry.distanceKm === null) {
            slot.textContent = '';
            slot.hidden = true;
          } else {
            slot.textContent = formatDistance(self.copy.distance, entry.distanceKm);
            slot.hidden = false;
          }
        }
        return entry.item;
      });

      if (this.notice) {
        this.notice.textContent = '';
        this.notice.hidden = true;
      }
      if (this.geoLabel) this.geoLabel.textContent = this.copy.nearby_done || '';
      this.setAttribute('data-twc-nearby', 'on');

      this.page = 1;
      this.render(true);
    }

    showNotice(code) {
      if (this.geoLabel) this.geoLabel.textContent = this.copy.nearby || '';
      if (!this.notice) return;
      var message = this.copy['geo_' + code] || this.copy.geo_unavailable || '';
      if (message === '') {
        this.notice.textContent = '';
        this.notice.hidden = true;
        return;
      }
      // Unhide FIRST: a live region that gains its text while hidden is never
      // announced.
      this.notice.hidden = false;
      this.notice.textContent = message;
    }
  };

  if (global.customElements) {
    global.customElements.define('twc-clubhouse-index', TwcClubhouseIndex);
  }
})(window);
